import React, { useEffect, useState } from "react";
import { useChat } from "../../contexts/ChatContext";
import { useAuth } from "../../contexts/AuthContext";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { Avatar, Badge, List, Input, Skeleton } from "antd";
import { SearchOutlined, MessageOutlined } from "@ant-design/icons";

const { Search } = Input;

const ChatList = ({ onSelectConversation }) => {
  const { conversations, unreadCount, fetchConversations, isConnected } = useChat();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadConversations = async () => {
      try {
        setLoading(true);
        await fetchConversations(true);
      } catch (error) {
        console.error("Erreur de chargement:", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (conversations.length === 0) {
      loadConversations();
    } else {
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [conversations.length, fetchConversations]);

  const filteredConversations = conversations
    .map((conv) => {
      const isClient = user?.id === conv.client_id;
      const otherUser = isClient
        ? {
            id: conv.mover_id,
            firstName: conv.mover_first_name,
            lastName: conv.mover_last_name,
            companyName: conv.mover_company_name || null,
            isMover: true,
          }
        : {
            id: conv.client_id,
            firstName: conv.client_first_name,
            lastName: conv.client_last_name,
            companyName: null,
            isMover: false,
          };

      return {
        ...conv,
        otherUser,
        lastMessage: conv.last_message_content
          ? {
              content: conv.last_message_content,
              createdAt: conv.last_message_at,
              senderId: conv.last_message_sender_id,
              isMine: conv.last_message_sender_id === user?.id,
            }
          : null,
        unreadCount: parseInt(conv.unread_count) || 0,
      };
    })
    .filter((conv) => {
      if (!searchQuery) return true;
      const searchLower = searchQuery.toLowerCase();
      return (
        conv.otherUser.firstName?.toLowerCase().includes(searchLower) ||
        conv.otherUser.lastName?.toLowerCase().includes(searchLower) ||
        (conv.otherUser.companyName &&
          conv.otherUser.companyName.toLowerCase().includes(searchLower)) ||
        conv.lastMessage?.content?.toLowerCase().includes(searchLower)
      );
    });

  const getDisplayName = (u) => {
    if (u.companyName) return u.companyName;
    return [u.firstName, u.lastName].filter(Boolean).join(" ").trim() || "Utilisateur";
  };

  const getAvatarText = (u) => {
    if (u.companyName) return u.companyName.charAt(0).toUpperCase();
    return u.firstName ? u.firstName.charAt(0).toUpperCase() : "U";
  };

  return (
    <div className="h-full flex flex-col bg-[#1a1a1d] text-gray-200 border-r border-gray-700">
      {/* Header */}
      <div className="p-4 border-b border-gray-700 bg-[#111]">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gold-500">💬 Messages</h2>
          <Badge
            count={unreadCount}
            style={{ backgroundColor: "#d4af37" }}
            className="shadow-md"
          />
        </div>
        <Search
          placeholder="Rechercher..."
          prefix={<SearchOutlined className="text-gray-400" />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="mb-2 rounded-lg"
          style={{
            backgroundColor: "#2a2a2d",
            border: "1px solid #444",
          }}
        />
      </div>

      {/* Corps */}
      {loading ? (
        <div className="p-4">
          {[1, 2, 3].map((i) => (
            <Skeleton
              active
              key={i}
              avatar
              paragraph={{ rows: 1 }}
              className="mb-4"
              title={false}
            />
          ))}
        </div>
      ) : filteredConversations.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-gray-400">
          <MessageOutlined className="text-4xl mb-4 text-gold-500" />
          <p>Aucune conversation trouvée</p>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="mt-2 text-gold-500 hover:underline"
            >
              Effacer la recherche
            </button>
          )}
        </div>
      ) : (
        <List
          itemLayout="horizontal"
          dataSource={filteredConversations}
          className="flex-1 overflow-y-auto"
          renderItem={(conversation) => (
            <List.Item
              onClick={() => onSelectConversation(conversation)}
              className={`p-3 rounded-lg mx-2 my-1 cursor-pointer transition-all duration-200 ${
                conversation.unreadCount > 0
                  ? "bg-[#2a2a2d] border-l-4 border-gold-500 shadow-md"
                  : "hover:bg-[#2a2a2d]"
              }`}
            >
              <List.Item.Meta
                avatar={
                  <Badge count={conversation.unreadCount} offset={[0, 5]}>
                    <Avatar
                      size="large"
                      style={{
                        backgroundColor: "#d4af37",
                        color: "#111",
                        fontWeight: "bold",
                      }}
                    >
                      {getAvatarText(conversation.otherUser)}
                    </Avatar>
                  </Badge>
                }
                title={
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-100">
                      {getDisplayName(conversation.otherUser)}
                    </span>
                    <span className="text-xs text-gray-400">
                      {conversation.lastMessage &&
                        formatDistanceToNow(new Date(conversation.lastMessage.createdAt), {
                          addSuffix: true,
                          locale: fr,
                        })}
                    </span>
                  </div>
                }
                description={
                  <div className="truncate text-gray-400 italic">
                    {conversation.lastMessage
                      ? conversation.lastMessage.content
                      : "Aucun message"}
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}

      {/* Footer si non connecté */}
      {!isConnected && (
        <div className="p-2 text-center text-xs text-gold-500 bg-[#2a2a2d] border-t border-gray-700">
          Connexion au chat en cours...
        </div>
      )}
    </div>
  );
};

export default ChatList;
